// Route table. Phase 5 only ships the customer-app routes; /login is
// public, every other route is wrapped in RequireAuth.

/* eslint-disable react-refresh/only-export-components --
   This is a route table, not a Fast-Refreshable component module: it defines
   the lazy() page components alongside the exported `router` config. The
   react-refresh rule (which wants a file to export only components) does not
   apply here. */

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { RequireAuth } from '../components/RequireAuth';
import { ActivityConnectionProvider } from '../lib/ActivityConnectionProvider';
import { AgentConnectionProvider } from '../lib/AgentConnectionProvider';

// Eager: the public login page and the authed landing page are the two first
// paints, so loading them as separate chunks would only add a spinner to a cold
// start. The always-mounted shell + its providers stay eager for the same reason.
import { Login } from '../pages/Login/Login';
import { Impersonate } from '../pages/Impersonate/Impersonate';
import { MfaRequired } from '../pages/MfaRequired/MfaRequired';
import { Home } from '../pages/Home/Home';
// Back-compat redirect for the retired full-page agent audit — eager (it only
// renders a <Navigate> to the Copilot history blade), so no lazy chunk.
import { AuditRedirect } from '../pages/Copilot/AuditRedirect';
// Same idea for the retired /ask-ai paths (renamed to /ai-playground).
import { LegacyAskAiRedirect } from '../pages/Copilot/LegacyAskAiRedirect';

// Lazy: every other page is code-split into its own chunk, fetched on first
// navigation, so the entry bundle no longer carries code a given session may
// never reach. The heavy ones drag their libraries with them — Designer pulls
// Monaco + React Flow, Usage pulls Kendo Charts — out of the entry bundle. The
// pages use named exports, so map each to a `default` for React.lazy. A single
// <Suspense> boundary around the shell's <Outlet> (AppShell.tsx) catches them.
const Playground = lazy(() => import('../pages/Playground/Playground').then((m) => ({ default: m.Playground })));
const CopilotHistory = lazy(() => import('../pages/Copilot/CopilotHistory').then((m) => ({ default: m.CopilotHistory })));
const Inbox = lazy(() => import('../pages/Inbox/Inbox').then((m) => ({ default: m.Inbox })));
const Designer = lazy(() => import('../pages/Designer/Designer').then((m) => ({ default: m.Designer })));
const Workflows = lazy(() => import('../pages/Workflows/Workflows').then((m) => ({ default: m.Workflows })));
const Playbooks = lazy(() => import('../pages/Playbooks/Playbooks').then((m) => ({ default: m.Playbooks })));
const PlaybookBuilder = lazy(() => import('../pages/Playbooks/PlaybookBuilder').then((m) => ({ default: m.PlaybookBuilder })));
const PulsarsHome = lazy(() => import('../pages/StandingAgents/PulsarsHome').then((m) => ({ default: m.PulsarsHome })));
const PulsarCreate = lazy(() => import('../pages/StandingAgents/PulsarCreate').then((m) => ({ default: m.PulsarCreate })));
const PulsarWorkspace = lazy(() => import('../pages/StandingAgents/PulsarWorkspace').then((m) => ({ default: m.PulsarWorkspace })));
const AllRunHistory = lazy(() => import('../pages/Workflows/AllRunHistory').then((m) => ({ default: m.AllRunHistory })));
const Routines = lazy(() => import('../pages/Routines/Routines').then((m) => ({ default: m.Routines })));
const Deployments = lazy(() => import('../pages/Deployments/Deployments').then((m) => ({ default: m.Deployments })));
const Extensions = lazy(() => import('../pages/Extensions/Extensions').then((m) => ({ default: m.Extensions })));
const Companies = lazy(() => import('../pages/Companies/Companies').then((m) => ({ default: m.Companies })));
const Agents = lazy(() => import('../pages/Agents/Agents').then((m) => ({ default: m.Agents })));
const Knowledge = lazy(() => import('../pages/Knowledge/Knowledge').then((m) => ({ default: m.Knowledge })));
const Runners = lazy(() => import('../pages/Runners/Runners').then((m) => ({ default: m.Runners })));
const RunnerDetail = lazy(() => import('../pages/Runners/RunnerDetail').then((m) => ({ default: m.RunnerDetail })));
const Overview = lazy(() => import('../pages/Settings/Overview').then((m) => ({ default: m.Overview })));
const Usage = lazy(() => import('../pages/Settings/Usage').then((m) => ({ default: m.Usage })));
const Team = lazy(() => import('../pages/Settings/Team').then((m) => ({ default: m.Team })));
const Integrations = lazy(() => import('../pages/Settings/Integrations').then((m) => ({ default: m.Integrations })));
const PersonalSettings = lazy(() => import('../pages/PersonalSettings/PersonalSettings').then((m) => ({ default: m.PersonalSettings })));
const Preferences = lazy(() => import('../pages/PersonalSettings/Preferences').then((m) => ({ default: m.Preferences })));
const Security = lazy(() => import('../pages/PersonalSettings/Security').then((m) => ({ default: m.Security })));
const Notifications = lazy(() => import('../pages/PersonalSettings/Notifications').then((m) => ({ default: m.Notifications })));
// Teams personal tab (standing-agents D13): its own compact shell, outside RequireAuth —
// it signs in with Teams SSO, not the web login.
const TeamsTab = lazy(() => import('../pages/TeamsTab/TeamsTab').then((m) => ({ default: m.TeamsTab })));
const ChangePassword = lazy(() => import('../pages/PersonalSettings/ChangePassword').then((m) => ({ default: m.ChangePassword })));

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  // Staff support-login landing — public (outside RequireAuth): redeems the
  // one-time handoff token to set the crauto-auth cookie on this origin, then
  // navigates into the app.
  { path: '/impersonate', element: <Impersonate /> },
  // MFA challenge — top-level (outside RequireAuth) so it renders without the
  // app shell / live hub providers. RequireAuth redirects MFA-pending sessions
  // here; the page itself bounces anonymous → /login and satisfied → the app.
  { path: '/mfa-required', element: <MfaRequired /> },
  {
    path: '/teams/tab',
    element: (
      <Suspense fallback={null}>
        <TeamsTab />
      </Suspense>
    ),
  },
  {
    element: (
      <RequireAuth>
        <ActivityConnectionProvider>
          {/* Observer agent connection drives the shell's "Action Required" badge in
              real time. `observer` means it joins the tenant group for live events
              but never marks the session hot, so the always-mounted shell doesn't
              pin runners hot — the interactive (hot) provider stays scoped to the
              agent pages below, where it overrides this context for its subtree. */}
          <AgentConnectionProvider observer>
            <AppShell />
          </AgentConnectionProvider>
        </ActivityConnectionProvider>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Home /> },
      // The agent hub keeps the tenant session-hot (3s runner poll cadence), so
      // it is scoped to ONLY the agent pages that consume it rather than the
      // whole shell — browsing unrelated pages must not pin runners hot.
      // The AI Playground is conversations when planning is on (standing.enabled); the
      // Playground page falls back to the legacy one-shot <Copilot /> while it's off.
      {
        path: 'ai-playground',
        element: (
          <AgentConnectionProvider>
            <Playground />
          </AgentConnectionProvider>
        ),
      },
      {
        path: 'ai-playground/c/:conversationId',
        element: (
          <AgentConnectionProvider>
            <Playground />
          </AgentConnectionProvider>
        ),
      },
      {
        path: 'ai-playground/history',
        element: (
          <AgentConnectionProvider>
            <CopilotHistory />
          </AgentConnectionProvider>
        ),
      },
      // Retired /ask-ai paths (the page is now the AI Playground). Published KB
      // articles and old bookmarks still point here, and ?runId= deep links must
      // survive the hop — hence the query-preserving redirect. The history route
      // is listed first so it wins over the bare one.
      { path: 'ask-ai/history', element: <LegacyAskAiRedirect to="/ai-playground/history" /> },
      { path: 'ask-ai', element: <LegacyAskAiRedirect to="/ai-playground" /> },
      {
        path: 'inbox',
        element: (
          <AgentConnectionProvider>
            <Inbox />
          </AgentConnectionProvider>
        ),
      },
      // Retired: the full-page agent audit is now the Copilot history blade.
      // Redirect old links to the history page with a `?runId=` deep-link.
      { path: 'agents/runs/:runId/audit', element: <AuditRedirect /> },
      { path: 'workflows', element: <Workflows /> },
      // Playbooks — the kanban home plus the four-question builder. /new
      // creates; /:playbookId edits (the builder also hosts the Runs tab).
      // Orion Pulsars (standing agents). Live updates ride the shell's observer
      // agent connection, so these pages don't mount the hot provider. The
      // batch deep link is listed before the optional-tab route.
      { path: 'pulsars', element: <PulsarsHome /> },
      { path: 'pulsars/new', element: <PulsarCreate /> },
      { path: 'pulsars/:id/proposals/:batchId', element: <PulsarWorkspace /> },
      { path: 'pulsars/:id/:tab?', element: <PulsarWorkspace /> },
      { path: 'playbooks', element: <Playbooks /> },
      { path: 'playbooks/new', element: <PlaybookBuilder /> },
      { path: 'playbooks/:playbookId', element: <PlaybookBuilder /> },
      // Run history — the "History" nav surface. Lists every run across all
      // workflows; a `?workflowId=` query param scopes it to one workflow (the
      // Workflows list "History" button and the in-grid workflow link).
      { path: 'runs', element: <AllRunHistory /> },
      { path: 'routines', element: <Routines /> },
      { path: 'designer', element: <Designer /> },
      { path: 'designer/:workflowId', element: <Designer /> },
      // Extensions tabs are deep-linkable: /extensions (Browse), /extensions/installed,
      // /extensions/custom. The :tab param drives the selected tab in <Extensions/>.
      { path: 'extensions', element: <Extensions /> },
      { path: 'extensions/:tab', element: <Extensions /> },
      { path: 'companies', element: <Companies /> },
      // Agents tabs are deep-linkable: /agents (Browse), /agents/installed,
      // /agents/custom. The :tab param drives the selected tab in <Agents/>.
      { path: 'agents', element: <Agents /> },
      { path: 'agents/:tab', element: <Agents /> },
      // Knowledge base — the DATA section's page. Optional `:docId` deep-links a
      // selected document; the tree pane owns folder selection in-page.
      { path: 'knowledge', element: <Knowledge /> },
      { path: 'knowledge/doc/:docId', element: <Knowledge /> },
      { path: 'deployments', element: <Deployments /> },
      { path: 'runners', element: <Runners /> },
      { path: 'runners/:runnerIdentityId', element: <RunnerDetail /> },
      // Settings area — the AppShell swaps the left sidebar to the settings
      // contextual nav for any /settings* path.
      { path: 'settings', element: <Overview /> },
      { path: 'settings/usage', element: <Usage /> },
      { path: 'settings/team', element: <Team /> },
      { path: 'settings/integrations', element: <Integrations /> },
      // Personal Settings — opened from the top-right avatar/email; its own
      // contextual sidebar + a Logout button in the page header.
      {
        path: 'personal-settings',
        element: <PersonalSettings />,
        children: [
          { index: true, element: <Preferences /> },
          { path: 'security', element: <Security /> },
          { path: 'notifications', element: <Notifications /> },
          { path: 'password', element: <ChangePassword /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
